import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { useApiMutation, useMe } from "../api/hooks";
import { LogoMark } from "../components/Logo";
import { ErrorState, Footer, Loading } from "../components/ui";
import { useToast } from "../components/Toasts";
import { daysLeft, nextMondayCheck } from "../lib/format";

/** Wordmark. Signed in, the second line is the workspace; signed out, "by FormOne". */
function BrandLink({ to = "/vendors", sub }: { to?: string; sub?: string }) {
  return (
    <NavLink to={to} className={`brand${sub ? "" : " public"}`} aria-label="CheckOne home">
      <LogoMark size={sub ? 28 : 32} />
      <span className="brand-name">
        Check<span className="brand-one">One</span>
      </span>
      <span className="brand-sub">{sub ?? "by FormOne"}</span>
    </NavLink>
  );
}

/** Right side of the header: trial days while on trial, otherwise the next scheduled check. */
function HeaderAside({ status, trialUntil }: { status: string; trialUntil: string | null }) {
  if (status === "trial" && trialUntil) {
    const left = daysLeft(trialUntil);
    return (
      <span className="topbar-aside">
        {left > 0 ? `Trial · ${left} ${left === 1 ? "day" : "days"} left` : "Trial ended"}
      </span>
    );
  }
  if (status === "paused" || status === "cancelled")
    return <span className="topbar-aside">Monday messages paused</span>;
  return <span className="topbar-aside">Next check {nextMondayCheck()}</span>;
}

/** Signed-in layout: requires a session, shows the tenant and a way back from a client workspace. */
export function AppShell() {
  const me = useMe();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const logout = useApiMutation("auth.logout");
  const switchBack = useApiMutation("clients.switch");
  // On a phone the menu scrolls sideways: keep the current page's item in view.
  useEffect(() => {
    document
      .querySelector('.nav a[aria-current="page"]')
      ?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }, [location.pathname, me.isPending]);

  if (me.isPending) return <Loading label="Signing you in…" />;
  if (me.error?.code === "unauthenticated")
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (me.error) return <ErrorState error={me.error} retry={() => void me.refetch()} />;

  const { user, tenant, home_tenant, is_admin } = me.data.me;
  const inClient = tenant.id !== home_tenant.id;
  const practitioner = home_tenant.kind === "practitioner";

  return (
    <div className="shell">
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <header className="topbar">
        <div className="topbar-inner">
          <BrandLink sub={tenant.name} />
          <nav className="nav" aria-label="Main">
            {practitioner && <NavLink to="/clients">Clients</NavLink>}
            <NavLink to="/vendors">Vendors</NavLink>
            <NavLink to="/runs">This week</NavLink>
            <NavLink to="/payment-checks">Payment run check</NavLink>
            <NavLink to="/upload">Upload</NavLink>
            <NavLink to="/settings">Settings</NavLink>
            {is_admin && <NavLink to="/admin">Admin</NavLink>}
          </nav>
          <div className="tenant-chip">
            <HeaderAside status={tenant.status} trialUntil={tenant.trial_until} />
            <button
              className="btn ghost small"
              title={`Signed in as ${user.email}`}
              onClick={() =>
                logout.mutate(
                  {},
                  {
                    onSuccess: () => {
                      qc.clear();
                      void navigate("/login");
                    },
                    onError: (e) => toast.error(e),
                  },
                )
              }
            >
              Sign out
            </button>
          </div>
        </div>
      </header>
      {inClient && (
        <div className="switch-banner" role="region" aria-label="Client workspace">
          <div className="inner">
            <span>
              You are working inside <strong>{tenant.name}</strong>, a client of {home_tenant.name}.
            </span>
            <button
              className="btn small outline"
              disabled={switchBack.isPending}
              onClick={() =>
                switchBack.mutate(
                  { params: { id: home_tenant.id } },
                  {
                    onSuccess: () => {
                      qc.clear();
                      void navigate("/clients");
                    },
                    onError: (e) => toast.error(e),
                  },
                )
              }
            >
              Back to {home_tenant.name}
            </button>
          </div>
        </div>
      )}
      <main id="main" tabIndex={-1}>
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

/** Layout for pages that need no session: sign-in, sign-up and the signed run page. */
export function PublicShell() {
  const { pathname } = useLocation();
  return (
    <div className="shell">
      <header className="topbar">
        <div className="topbar-inner">
          <BrandLink to="/login" />
          {pathname === "/signup" && (
            <span className="topbar-aside">
              Already set up? <Link to="/login">Sign in</Link>
            </span>
          )}
        </div>
      </header>
      <main id="main">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
